import React from 'react';
import { Typography, Collapse, Input, Button } from 'antd';
import { 
  QuestionCircleOutlined, 
  SearchOutlined, 
  DollarCircleOutlined,
  ClockCircleOutlined,
  SafetyOutlined,
  UserOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';

const { Title, Paragraph } = Typography;
const { Panel } = Collapse;
const { Search } = Input;

const FAQs: React.FC = () => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const faqData = [
    {
      category: 'General Information',
      icon: <QuestionCircleOutlined />,
      questions: [
        {
          question: 'What is JEJELY ULTIMATE LIMITED?',
          answer: 'JEJELY ULTIMATE LIMITED is a licensed microfinance institution in Nigeria that delivers convenient personal loan services to individuals through its mobile app, Fairloner.'
        },
        {
          question: 'Are you a licensed financial institution?',
          answer: 'Yes, JEJELY ULTIMATE LIMITED is a licensed and regulated microfinance institution in Nigeria, committed to providing safe and compliant personal loan services.'
        },
        {
          question: 'What services do you offer?',
          answer: 'We offer personal loans and financial advisory services. Our loan products are designed to meet various financial needs of individuals.'
        },
        {
          question: 'How can I contact JEJELY ULTIMATE LIMITED?',
          answer: 'You can contact us through phone at 08158120510, email at fairlonerhelp@gmail.com. Our customer service team is available to assist you with all your inquiries.'
        }
      ]
    },
    {
      category: 'Loan Application',
      icon: <DollarCircleOutlined />,
      questions: [
        {
          question: 'How do I apply for a loan?',
          answer: (
            <div>
              <p style={{ marginBottom: '15px' }}><strong>1. Download & Install the App</strong></p>
              <p style={{ marginBottom: '15px', paddingLeft: '20px' }}>Go to the Apple App Store and search for Fairloner, then download and install it on your phone.</p>
              
              <p style={{ marginBottom: '15px' }}><strong>2. Create an Account</strong></p>
              <p style={{ marginBottom: '15px', paddingLeft: '20px' }}>Register with your mobile number and complete the verification process.</p>
              
              <p style={{ marginBottom: '15px' }}><strong>3. Provide Required Information</strong></p>
              <p style={{ marginBottom: '15px', paddingLeft: '20px' }}>Fill in your personal details, employment information, emergency contacts, and bank account information.</p>
              
              <p style={{ marginBottom: '15px' }}><strong>4. Submit Your Application</strong></p>
              <p style={{ marginBottom: '15px', paddingLeft: '20px' }}>Choose your preferred loan amount and repayment period, then submit your loan request.</p>
              
              <p style={{ marginBottom: '15px' }}><strong>5. Wait for Review</strong></p>
              <p style={{ marginBottom: '15px', paddingLeft: '20px' }}>Our system will process your application and notify you of the approval status within a short time.</p>
              
              <p style={{ marginBottom: '15px' }}><strong>6. Receive Your Loan</strong></p>
              <p style={{ paddingLeft: '20px' }}>Once approved, the loan amount will be disbursed directly to your bank account.</p>
            </div>
          )
        },
        {
          question: 'What are the eligibility requirements for a loan?',
          answer: 'To be eligible for a loan, you must be a Nigerian citizen or legal resident, at least 18 years old, have a valid means of identification, and a bank account in Nigeria. Good credit history is also considered.'
        },
        {
          question: 'How long does the loan approval process take?',
          answer: 'Our loan approval process is designed to be quick and efficient. Typically, loan applications are processed within 24 hours after all required documents are submitted and verified.'
        },
        {
          question: 'What is the minimum and maximum loan amount?',
          answer: 'We offer flexible loan amounts ranging from ₦2,000 to ₦300,000 for personal loans. The exact amount you can borrow depends on your income, creditworthiness, and repayment capacity.'
        }
      ]
    },
    {
      category: 'Interest Rates & Fees',
      icon: <ClockCircleOutlined />,
      questions: [
        {
          question: 'What are your interest rates?',
          answer: 'Our interest rates are competitive and based on risk assessment. Rates vary depending on the loan amount, term, and borrower\'s credit profile. We provide transparent information about all rates and fees before loan approval.'
        },
        {
          question: 'Are there any hidden fees?',
          answer: 'No, we maintain complete transparency in our fee structure. All fees, charges, and interest rates are clearly disclosed before loan approval. There are no hidden fees or surprise charges.'
        },
        {
          question: 'Can I pay off my loan early?',
          answer: 'Yes, you can pay off your loan early without any prepayment penalties. Early repayment can help you improve your credit profile.'
        },
        {
          question: 'What happens if I miss a payment?',
          answer: 'If you miss a payment, you may be charged a late fee and it could affect your ability to borrow in the future. Please reach out to us as soon as possible if you are having trouble repaying.'
        }
      ]
    },
    {
      category: 'Repayment',
      icon: <UserOutlined />,
      questions: [
        {
          question: 'How do I make loan repayments?',
          answer: 'To make a loan repayment, simply log in to the Fairloner app and go to the Bills or Loan Record section. Select the loan you want to repay and complete the payment using the integrated online payment options or by transferring to the designated bank account.'
        },
        {
          question: 'What if I have difficulty making payments?',
          answer: 'If you experience financial difficulties, please contact us immediately. We offer various assistance programs and can work with you to restructure your loan or provide temporary relief options.'
        }
      ]
    },
    {
      category: 'Security & Privacy',
      icon: <SafetyOutlined />,
      questions: [
        {
          question: 'Is my personal information secure?',
          answer: 'Yes, we implement strict security measures to protect your personal and financial information. We comply with all data protection regulations and use secure systems to store and process customer data.'
        },
        {
          question: 'How do you protect customer data?',
          answer: 'We use encryption, secure servers, and strict access controls to protect customer data. Our staff are trained on data protection practices, and we regularly audit our security systems.'
        },
        {
          question: 'Will my information be shared with third parties?',
          answer: 'We do not share your personal information with third parties except as required by law or with your explicit consent. We maintain strict confidentiality of all customer information.'
        },
        {
          question: 'How can I access my account information?',
          answer: 'Log in to the Fairloner app and go to Account to view your account information, including personal details and loan history.'
        }
      ]
    }
  ];

  const filteredFAQs = faqData.map(category => ({
    ...category,
    questions: category.questions.filter(q => {
      const questionText = q.question.toLowerCase();
      const answerText = typeof q.answer === 'string' 
        ? q.answer.toLowerCase() 
        : q.answer.toString().toLowerCase();
      const searchLower = searchTerm.toLowerCase();
      
      return questionText.includes(searchLower) || answerText.includes(searchLower);
    })
  })).filter(category => category.questions.length > 0);

  return (
    <div>
      {/* Page Header */}
      <div className="page-header">
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Title level={1} style={{ color: 'white', marginBottom: '10px' }}>
            Frequently Asked Questions
          </Title>
          <Paragraph style={{ color: 'white', fontSize: '1.1rem' }}>
            Find answers to common questions about our services
          </Paragraph>
        </div>
      </div>

      {/* Search Section */}
      <div style={{ padding: '40px 0', background: '#f8f9fa' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px', textAlign: 'center' }}>
          <Title level={2}>Search FAQs</Title>
          <Paragraph style={{ fontSize: '1.1rem', marginBottom: '30px' }}>
            Can't find what you're looking for? Use the search below or contact us directly.
          </Paragraph>
          <Search
            placeholder="Search frequently asked questions..."
            allowClear
            enterButton={<SearchOutlined />}
            size="large"
            style={{ maxWidth: '600px' }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* FAQ Categories */}
      <div style={{ padding: '60px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          {filteredFAQs.length > 0 ? (
            filteredFAQs.map((category, index) => (
              <div key={index} style={{ marginBottom: '40px' }}>
                <Title level={2} style={{ marginBottom: '30px' }}>
                  {category.icon} {category.category}
                </Title>
                <Collapse size="large" style={{ marginBottom: '30px' }}>
                  {category.questions.map((faq, faqIndex) => (
                    <Panel header={faq.question} key={faqIndex}>
                      <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                        {faq.answer}
                      </Paragraph>
                    </Panel>
                  ))}
                </Collapse>
              </div>
            ))
          ) : (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <QuestionCircleOutlined style={{ fontSize: '4rem', color: '#ccc', marginBottom: '20px' }} />
              <Title level={3}>No FAQs Found</Title>
              <Paragraph style={{ fontSize: '1.1rem', color: '#666' }}>
                No questions match your search term. Try a different search or contact us directly.
              </Paragraph>
            </div>
          )}
        </div>
      </div>

      {/* Contact Section */}
      <div style={{ background: '#f8f9fa', padding: '60px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <Title level={2}>Still Have Questions?</Title>
            <Paragraph style={{ fontSize: '1.1rem', color: '#666', marginBottom: '30px' }}>
              If you couldn't find the answer you're looking for, our customer service team is here to help.
            </Paragraph>
            <Button type="primary" size="large">
              <Link to="/contact" style={{ color: 'white' }}>Contact Us</Link>
            </Button>
          </div>
        </div>
      </div>

      {/* Business Hours */}
      {/* <div style={{ padding: '60px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Row gutter={[40, 40]} align="middle">
            <Col xs={24} lg={12}>
              <Title level={2}>Business Hours</Title>
              <Paragraph style={{ fontSize: '1.1rem', lineHeight: '1.8' }}>
                Our customer service team is available during the following hours to assist you 
                with all your questions and concerns.
              </Paragraph>
              <div style={{ background: '#f8f9fa', padding: '30px', borderRadius: '8px' }}>
                <div style={{ marginBottom: '15px' }}>
                  <Text strong>Monday - Friday:</Text> 8:00 AM - 6:00 PM
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <Text strong>Saturday:</Text> 9:00 AM - 4:00 PM
                </div>
                <div style={{ marginBottom: '15px' }}>
                  <Text strong>Sunday:</Text> Closed
                </div>
                <div style={{ color: '#1890ff', fontStyle: 'italic' }}>
                  Emergency support available 24/7 for existing customers
                </div>
              </div>
            </Col>
            <Col xs={24} lg={12}>
              <Card style={{ textAlign: 'center', padding: '40px 20px' }}>
                <ClockCircleOutlined style={{ fontSize: '4rem', color: '#1890ff', marginBottom: '20px' }} />
                <Title level={3}>Quick Response</Title>
                <Paragraph style={{ fontSize: '1.1rem' }}>
                  We aim to respond to all inquiries within 24 hours during business days. 
                  For urgent matters, please call us directly.
                </Paragraph>
              </Card>
            </Col>
          </Row>
        </div>
      </div> */}
    </div>
  );
};

export default FAQs;
